// client/src/coms/views/canvas_parts/canvas_room_view_parts/importedBrushRenderer.js
import { clipContextToSelection } from './selectionUtils';
import { sanitizeImportedBrushBlobPreview } from './importedBrushUtils';
import { isV2ImportedBrush, planDabs } from './brushImport/dabPlanner.js';

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function clampOpacity(value) {
  return clamp(Number(value), 0, 1);
}

function pressureValue(point, fallback = 1) {
  const pressure = Number(point && point.pressure);
  if (!Number.isFinite(pressure) || pressure <= 0) return fallback;
  return clamp(pressure, 0.02, 1);
}

function createBlobPath(blobPreview) {
  const blob = sanitizeImportedBrushBlobPreview(blobPreview);
  if (!blob || !blob.points || blob.points.length < 3) return null;
  const path = new Path2D();
  blob.points.forEach((point, index) => {
    if (index === 0) path.moveTo(point.x, point.y);
    else path.lineTo(point.x, point.y);
  });
  path.closePath();
  return { path, blob };
}

function fallbackDab(ctx, x, y, radius) {
  ctx.beginPath();
  ctx.arc(x, y, Math.max(0.5, radius), 0, Math.PI * 2);
  ctx.closePath();
  ctx.fill();
}

function pointAngle(startPoint, endPoint, fallback = 0) {
  if (!startPoint || !endPoint) return fallback;
  const dx = Number(endPoint.x) - Number(startPoint.x);
  const dy = Number(endPoint.y) - Number(startPoint.y);
  if (!Number.isFinite(dx) || !Number.isFinite(dy) || Math.hypot(dx, dy) < 0.001) return fallback;
  return Math.atan2(dy, dx);
}

function randomUnit(seed) {
  const x = Math.sin((Number(seed) || 1) * 12.9898) * 43758.5453123;
  return x - Math.floor(x);
}

function randomAngle(seed) {
  return randomUnit(seed) * Math.PI * 2;
}


function interpolateImportedPoint(a, b, t) {
  const safeT = clamp(Number(t) || 0, 0, 1);
  const ax = Number(a && a.x) || 0;
  const ay = Number(a && a.y) || 0;
  const bx = Number(b && b.x) || ax;
  const by = Number(b && b.y) || ay;
  const point = {
    x: ax + ((bx - ax) * safeT),
    y: ay + ((by - ay) * safeT),
  };

  ['pressure', 'tiltX', 'tiltY', 'twist', 'tangentialPressure', 'altitudeAngle', 'azimuthAngle'].forEach((key) => {
    const av = Number(a && a[key]);
    const bv = Number(b && b[key]);
    if (Number.isFinite(av) || Number.isFinite(bv)) {
      const safeA = Number.isFinite(av) ? av : Number.isFinite(bv) ? bv : 0;
      const safeB = Number.isFinite(bv) ? bv : safeA;
      point[key] = safeA + ((safeB - safeA) * safeT);
    }
  });

  if (b && b.pencilOrientation) point.pencilOrientation = b.pencilOrientation;
  else if (a && a.pencilOrientation) point.pencilOrientation = a.pencilOrientation;

  return point;
}

function midpointImportedPoint(a, b) {
  return interpolateImportedPoint(a, b, 0.5);
}

function quadraticImportedPoint(start, control, end, t) {
  const startToControl = interpolateImportedPoint(start, control, t);
  const controlToEnd = interpolateImportedPoint(control, end, t);
  return interpolateImportedPoint(startToControl, controlToEnd, t);
}

function buildQuadraticImportedPoints(start, control, end, options) {
  const approximateLength = Math.hypot((control.x || 0) - (start.x || 0), (control.y || 0) - (start.y || 0)) + Math.hypot((end.x || 0) - (control.x || 0), (end.y || 0) - (control.y || 0));
  const safeSize = Math.max(1, Number(options && options.size) || 1);
  const isEraser = !!(options && options.isEraser);
  const sampleSpacing = isEraser
    ? Math.max(2, Math.min(18, safeSize * 0.35))
    : Math.max(1, Math.min(10, safeSize * 0.18));
  const steps = Math.max(2, Math.min(isEraser ? 48 : 96, Math.ceil(approximateLength / sampleSpacing)));
  const points = [];

  for (let index = 0; index <= steps; index += 1) {
    points.push(quadraticImportedPoint(start, control, end, index / steps));
  }

  return points;
}

function drawImportedCurveUsingSegment(segmentRenderer, ctx, previousPreviousPoint, previousPoint, currentPoint, options, selection, dabIndexStart = 0) {
  if (!ctx || !previousPreviousPoint || !previousPoint || !currentPoint) return dabIndexStart;

  if (options && options.isEraser) {
    return segmentRenderer(ctx, previousPoint, currentPoint, options, selection, dabIndexStart, false);
  }

  const curveStart = midpointImportedPoint(previousPreviousPoint, previousPoint);
  const curveEnd = midpointImportedPoint(previousPoint, currentPoint);
  const points = buildQuadraticImportedPoints(curveStart, previousPoint, curveEnd, options);
  let dabIndex = Math.max(0, Number(dabIndexStart) || 0);

  for (let index = 1; index < points.length; index += 1) {
    dabIndex = segmentRenderer(ctx, points[index - 1], points[index], options, selection, dabIndex, false);
  }

  return dabIndex;
}

function drawImportedFinalTailUsingSegment(segmentRenderer, ctx, previousPoint, currentPoint, options, selection, dabIndexStart = 0) {
  if (!ctx || !previousPoint || !currentPoint) return dabIndexStart;
  const tailStart = midpointImportedPoint(previousPoint, currentPoint);
  return segmentRenderer(ctx, tailStart, currentPoint, options, selection, dabIndexStart, false);
}

const tintedTipCanvasCache = new WeakMap();

const bitmapTipCanvasCache = new WeakMap();

function drawSvgDotPreview(ctx, brushPreview, params, size, colour) {
  const blob = sanitizeImportedBrushBlobPreview(brushPreview);
  const svgPreview = blob && blob.svgPreview ? blob.svgPreview : null;
  const dots = svgPreview && Array.isArray(svgPreview.dots) ? svgPreview.dots : [];
  if (!svgPreview || !dots.length) return false;

  const width = Math.max(1, Math.min(32, Math.round(Number(svgPreview.width) || 1)));
  const height = Math.max(1, Math.min(32, Math.round(Number(svgPreview.height) || 1)));
  const scale = size / Math.max(width, height);
  const seed = Number(params && params.currentDabSeed) || Number(params && params.strokeSeed) || 1;

  ctx.save();
  ctx.fillStyle = colour;
  dots.forEach((dot, index) => {
    const dotAlpha = clampOpacity(dot && dot.a);
    if (dotAlpha <= 0.002) return;

    const keepNoise = randomUnit(seed + index * 97 + 19);
    const keepChance = clamp(0.32 + dotAlpha * 0.82, 0.18, 0.92);
    if (keepNoise > keepChance) return;

    const jitterX = (randomUnit(seed + index * 131 + 43) - 0.5) * 1.1;
    const jitterY = (randomUnit(seed + index * 173 + 71) - 0.5) * 1.1;
    const x = ((Number(dot.x) || 0) - width / 2 + jitterX) * scale;
    const y = ((Number(dot.y) || 0) - height / 2 + jitterY) * scale;
    const radius = Math.max(0.28, (Number(dot.r) || 0.5) * scale * (0.72 + randomUnit(seed + index * 211 + 89) * 0.72));

    ctx.globalAlpha = clamp(dotAlpha * (0.2 + randomUnit(seed + index * 257 + 101) * 0.58), 0.012, 0.42);
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fill();
  });
  ctx.restore();
  return true;
}

function getBitmapTipCanvas(brushPreview, colour) {
  const blob = sanitizeImportedBrushBlobPreview(brushPreview);
  const bitmap = blob && blob.bitmap ? blob.bitmap : null;
  if (!bitmap || !Array.isArray(bitmap.pixels)) return null;

  const fillColour = String(colour || '#000000');
  let byColour = bitmapTipCanvasCache.get(blob);
  if (!byColour) {
    byColour = new Map();
    bitmapTipCanvasCache.set(blob, byColour);
  }

  const key = fillColour;
  if (byColour.has(key)) return byColour.get(key);

  const width = Math.max(1, Math.min(32, Math.round(Number(bitmap.width) || 1)));
  const height = Math.max(1, Math.min(32, Math.round(Number(bitmap.height) || 1)));
  const pixels = bitmap.pixels.slice(0, width * height);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const canvasCtx = canvas.getContext('2d', { willReadFrequently: true });
  if (!canvasCtx) return null;

  const imageData = canvasCtx.createImageData(width, height);
  for (let index = 0; index < width * height; index += 1) {
    const alpha = Math.max(0, Math.min(255, Math.round(Number(pixels[index]) || 0)));
    imageData.data[index * 4 + 0] = 0;
    imageData.data[index * 4 + 1] = 0;
    imageData.data[index * 4 + 2] = 0;
    imageData.data[index * 4 + 3] = alpha;
  }
  canvasCtx.putImageData(imageData, 0, 0);

  const tintedCanvas = getTintedTipCanvas(canvas, fillColour) || canvas;
  byColour.set(key, tintedCanvas);
  return tintedCanvas;
}

const softnessAdjustedTipCanvasCache = new WeakMap();

const procreateGrainAdjustedTipCanvasCache = new WeakMap();

function getCanvasImageDataSafe(canvas) {
  if (!canvas || !canvas.width || !canvas.height) return null;
  try {
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    return ctx ? ctx.getImageData(0, 0, canvas.width, canvas.height) : null;
  } catch {
    return null;
  }
}

function getProcreateGrainedTipCanvas(alphaCanvas, grainCanvas, brush, seed) {
  if (!alphaCanvas || !grainCanvas || !grainCanvas.width || !grainCanvas.height) return alphaCanvas || null;
  const depth = clamp(Number(brush && brush.procreateTextureDepth) || 0, 0, 1);
  if (depth <= 0.002) return alphaCanvas;

  let byGrain = procreateGrainAdjustedTipCanvasCache.get(alphaCanvas);
  if (!byGrain) {
    byGrain = new WeakMap();
    procreateGrainAdjustedTipCanvasCache.set(alphaCanvas, byGrain);
  }

  let byKey = byGrain.get(grainCanvas);
  if (!byKey) {
    byKey = new Map();
    byGrain.set(grainCanvas, byKey);
  }

  const scale = clamp(Number(brush && brush.procreateTextureScale) || 1, 0.05, 12);
  const movement = Number(brush && brush.procreateTextureMovement) || 0;
  const bucket = Math.abs(Math.floor((Number(seed) || 1) + movement * 97)) % 12;
  const key = [alphaCanvas.width, alphaCanvas.height, grainCanvas.width, grainCanvas.height, scale.toFixed(3), depth.toFixed(3), bucket].join(':');
  if (byKey.has(key)) return byKey.get(key);

  const tipData = getCanvasImageDataSafe(alphaCanvas);
  const grainData = getCanvasImageDataSafe(grainCanvas);
  if (!tipData || !grainData) return alphaCanvas;

  const out = document.createElement('canvas');
  out.width = alphaCanvas.width;
  out.height = alphaCanvas.height;
  const outCtx = out.getContext('2d', { willReadFrequently: true });
  if (!outCtx) return alphaCanvas;

  const next = outCtx.createImageData(out.width, out.height);
  const grainWidth = grainCanvas.width;
  const grainHeight = grainCanvas.height;
  const offsetX = Math.floor(randomUnit(bucket + 19) * grainWidth);
  const offsetY = Math.floor(randomUnit(bucket + 47) * grainHeight);
  const invScale = 1 / Math.max(0.05, scale);

  for (let y = 0; y < out.height; y += 1) {
    for (let x = 0; x < out.width; x += 1) {
      const index = (y * out.width + x) * 4;
      const baseAlpha = tipData.data[index + 3] / 255;
      if (baseAlpha <= 0.002) continue;
      const gx = Math.abs(Math.floor((x * invScale + offsetX) % grainWidth));
      const gy = Math.abs(Math.floor((y * invScale + offsetY) % grainHeight));
      const grainIndex = (gy * grainWidth + gx) * 4;
      const grainAlpha = grainData.data[grainIndex + 3] / 255;
      const multiplier = clamp((1 - depth * 0.86) + grainAlpha * depth * 0.98, 0, 1.35);
      const alpha = clamp(baseAlpha * multiplier, 0, 1);
      next.data[index + 0] = 0;
      next.data[index + 1] = 0;
      next.data[index + 2] = 0;
      next.data[index + 3] = Math.round(alpha * 255);
    }
  }

  outCtx.putImageData(next, 0, 0);
  byKey.set(key, out);
  return out;
}

function brushSoftnessPercent(options) {
  const value = Number(options && options.softness);
  if (!Number.isFinite(value)) return 50;
  return clamp(value, 0, 100);
}

function getSoftnessAdjustedTipCanvas(alphaCanvas, softnessPercent) {
  if (!alphaCanvas) return null;

  const softness = Math.round(clamp(Number(softnessPercent), 0, 100));
  if (softness === 50) return alphaCanvas;

  let bySoftness = softnessAdjustedTipCanvasCache.get(alphaCanvas);
  if (!bySoftness) {
    bySoftness = new Map();
    softnessAdjustedTipCanvasCache.set(alphaCanvas, bySoftness);
  }

  const key = String(softness);
  if (bySoftness.has(key)) return bySoftness.get(key);

  const width = Math.max(1, alphaCanvas.width || 1);
  const height = Math.max(1, alphaCanvas.height || 1);
  const out = document.createElement('canvas');
  out.width = width;
  out.height = height;
  const outCtx = out.getContext('2d', { willReadFrequently: true });

  if (!outCtx) return alphaCanvas;

  outCtx.clearRect(0, 0, width, height);
  outCtx.drawImage(alphaCanvas, 0, 0);

  try {
    const imageData = outCtx.getImageData(0, 0, width, height);
    const data = imageData.data;
    const hardAmount = clamp((50 - softness) / 50, 0, 1);
    const gamma = 1 - (hardAmount * 0.78);
    const cutoff = 2 + (hardAmount * 18);

    for (let index = 0; index < data.length; index += 4) {
      const alpha = data[index + 3];
      let nextAlpha = alpha;

      if (softness <= 0) {
        nextAlpha = alpha > 8 ? 255 : 0;
      } else if (hardAmount > 0) {
        if (alpha <= cutoff) {
          nextAlpha = 0;
        } else {
          const normalised = clamp(alpha / 255, 0, 1);
          nextAlpha = Math.round(Math.pow(normalised, gamma) * 255);
        }
      }

      data[index + 3] = clamp(nextAlpha, 0, 255);
    }

    outCtx.putImageData(imageData, 0, 0);
  } catch {
    outCtx.clearRect(0, 0, width, height);
    outCtx.drawImage(alphaCanvas, 0, 0);
  }

  const softAmount = clamp((softness - 50) / 50, 0, 1);
  if (softAmount > 0 && 'filter' in outCtx) {
    const blurred = document.createElement('canvas');
    blurred.width = width;
    blurred.height = height;
    const blurredCtx = blurred.getContext('2d');
    if (blurredCtx) {
      const blurRadius = Math.max(0.35, Math.min(8, Math.max(width, height) * 0.028 * softAmount));
      blurredCtx.clearRect(0, 0, width, height);
      blurredCtx.filter = `blur(${blurRadius}px)`;
      blurredCtx.drawImage(out, 0, 0);
      blurredCtx.filter = 'none';
      outCtx.clearRect(0, 0, width, height);
      outCtx.drawImage(blurred, 0, 0);
    }
  }

  bySoftness.set(key, out);
  return out;
}

function getPointPressure(point, fallback = 1) {
  return pressureValue(point, fallback);
}

function evaluateCurve(points, input, fallback = 1) {
  const curve = Array.isArray(points) ? points : [];
  const x = clamp(Number(input), 0, 1);
  if (!curve.length) return fallback;
  if (x <= curve[0].x) return curve[0].y;
  for (let index = 1; index < curve.length; index += 1) {
    const prev = curve[index - 1];
    const next = curve[index];
    if (x <= next.x) {
      const span = Math.max(0.0001, next.x - prev.x);
      const t = clamp((x - prev.x) / span, 0, 1);
      return prev.y + (next.y - prev.y) * t;
    }
  }
  return curve[curve.length - 1].y;
}

function pressureSizeMultiplier(point, options) {
  const brush = options && options.importedBrush ? options.importedBrush : options || {};
  if (!options || (options.pressureToSize === false || brush.pressureToSize === false)) return 1;
  const pressure = pressureValue(point, 1);
  return clamp(evaluateCurve(brush && brush.sizeCurve, pressure, pressure), 0.02, 4);
}

function pressureOpacityMultiplier(point, options) {
  const brush = options && options.importedBrush ? options.importedBrush : options || {};
  if (!options || (options.pressureToOpacity !== true && brush.pressureToOpacity !== true)) return 1;
  const pressure = pressureValue(point, 1);
  return clamp(evaluateCurve(brush && brush.opacityCurve, pressure, pressure), 0, 1);
}

function pressureFlowMultiplier(point, options) {
  const brush = options && options.importedBrush ? options.importedBrush : options || {};
  if (!options || (options.pressureToFlow !== true && brush.pressureToFlow !== true)) return 1;
  const pressure = pressureValue(point, 1);
  return clamp(evaluateCurve(brush && brush.flowCurve, pressure, pressure), 0, 1);
}

function brushPaintOpacity(options, point) {
  const brush = options && options.importedBrush ? options.importedBrush : options || {};
  const uiOpacity = clampOpacity(options && options.opacity == null ? 1 : options.opacity);
  const presetOpacity = clampOpacity(brush && brush.brushOpacity == null ? 1 : brush.brushOpacity);
  const flow = clampOpacity(brush && brush.flow == null ? 1 : brush.flow);
  return uiOpacity * presetOpacity * flow * pressureOpacityMultiplier(point, options) * pressureFlowMultiplier(point, options);
}

function tiltAngle(point, fallback = 0) {
  const tiltX = Number(point && point.tiltX);
  const tiltY = Number(point && point.tiltY);
  if (!Number.isFinite(tiltX) || !Number.isFinite(tiltY) || (Math.abs(tiltX) < 0.001 && Math.abs(tiltY) < 0.001)) return fallback;
  return Math.atan2(tiltY, tiltX);
}

function resolveRotation(rotationMode, point, angle, seed, fallbackAngle = 0) {
  if (rotationMode === 'random') return randomAngle(seed + 33);
  if (rotationMode === 'none') return 0;
  if (rotationMode === 'tilt') return tiltAngle(point, angle || fallbackAngle || 0);
  return angle || fallbackAngle || 0;
}

function getImportedBrushSpacing(options, point) {
  const brush = options && options.importedBrush ? options.importedBrush : {};
  const isEraser = !!(options && options.isEraser);
  const rawRatio = Number(brush.spacing != null ? brush.spacing : options && options.spacing);
  const baseRatio = isEraser
    ? Math.max(0.24, clamp(Number.isFinite(rawRatio) ? rawRatio : 0.24, 0.12, 2))
    : clamp(Number.isFinite(rawRatio) ? rawRatio : 0.08, 0.005, 2);
  const pressure = pressureValue(point, 1);
  const spacingMultiplier = !isEraser && (brush.pressureToSpacing === true || options && options.pressureToSpacing === true)
    ? clamp(evaluateCurve(brush.spacingCurve, pressure, pressure), 0.05, 4)
    : 1;
  const visualSize = Math.max(0.5, Number(options && options.size) || 1);
  const spacing = visualSize * baseRatio * spacingMultiplier;
  const minimumSpacing = isEraser ? Math.max(4, Math.min(28, visualSize * 0.28)) : 0.5;
  return Math.max(minimumSpacing, spacing);
}

function getStrokeRuntimeState(options) {
  if (!options || typeof options !== 'object') return null;
  const state = options.importedBrushStrokeState || options.brushStrokeState || null;
  return state && typeof state === 'object' ? state : null;
}

function resetStrokeRuntimeState(state, spacing) {
  if (!state) return;
  state.distanceToNext = Math.max(1, spacing);
  state.lastAngle = 0;
}

function markStrokeRuntimeProgress(state, remainingDistance) {
  if (!state) return;
  state.distanceToNext = Math.max(0.0001, remainingDistance);
}

function getTintedTipCanvas(alphaCanvas, colour) {
  if (!alphaCanvas) return null;
  const fillColour = String(colour || '#000000');
  let byColour = tintedTipCanvasCache.get(alphaCanvas);

  if (!byColour) {
    byColour = new Map();
    tintedTipCanvasCache.set(alphaCanvas, byColour);
  }

  const key = fillColour + ':' + alphaCanvas.width + 'x' + alphaCanvas.height;
  if (byColour.has(key)) return byColour.get(key);

  const out = document.createElement('canvas');
  out.width = Math.max(1, alphaCanvas.width || 1);
  out.height = Math.max(1, alphaCanvas.height || 1);
  const outCtx = out.getContext('2d');

  if (!outCtx) return alphaCanvas;

  outCtx.drawImage(alphaCanvas, 0, 0);
  outCtx.globalCompositeOperation = 'source-in';
  outCtx.fillStyle = fillColour;
  outCtx.fillRect(0, 0, out.width, out.height);

  byColour.set(key, out);
  return out;
}

export function renderImportedBrushBlobDab(ctx, params) {
  if (!ctx || !params) return;

  const point = params.point || { x: params.x, y: params.y, pressure: params.pressure };
  const x = Number(params.x != null ? params.x : point.x);
  const y = Number(params.y != null ? params.y : point.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return;

  const size = Math.max(0.5, (Number(params.size) || 1) * pressureSizeMultiplier(point, params));
  const opacity = brushPaintOpacity(params, point);
  const colour = String(params.color || params.rgba || '#000000');
  const isEraser = !!params.isEraser || params.tool === 'eraser';
  const prepared = createBlobPath(params.brushPreview);
  const rotation = Number(params.rotation) || 0;
  const bitmapCanvas = getBitmapTipCanvas(params.brushPreview, colour);

  ctx.save();
  ctx.globalCompositeOperation = isEraser ? 'destination-out' : 'source-over';
  ctx.globalAlpha = opacity;
  const softness = brushSoftnessPercent(params);
  const blobBlur = Math.max(0, (softness - 50) / 50) * size * 0.14;
  if (blobBlur > 0.15 && 'filter' in ctx) ctx.filter = `blur(${Math.min(12, blobBlur)}px)`;
  ctx.fillStyle = isEraser ? 'rgba(0,0,0,1)' : colour;
  ctx.translate(x, y);
  ctx.rotate(rotation + (Number(params.fixedAngle) || 0));

  const drewSvgPreview = drawSvgDotPreview(ctx, params.brushPreview, params, size, isEraser ? 'rgba(0,0,0,1)' : colour);
  if (!drewSvgPreview && bitmapCanvas) {
    ctx.imageSmoothingEnabled = false;
    if ('imageSmoothingQuality' in ctx) ctx.imageSmoothingQuality = 'low';
    const width = Math.max(1, bitmapCanvas.width || 1);
    const height = Math.max(1, bitmapCanvas.height || 1);
    const scale = size / Math.max(width, height);
    const drawWidth = width * scale;
    const drawHeight = height * scale;
    ctx.drawImage(bitmapCanvas, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
  } else if (!drewSvgPreview) {
    ctx.scale(size, size);
    if (prepared && prepared.path) {
      ctx.fill(prepared.path);
    } else {
      ctx.scale(1 / size, 1 / size);
      fallbackDab(ctx, 0, 0, size * 0.5);
    }
  }

  ctx.restore();
}

export function drawImportedBrushBlobSegment(ctx, startPoint, endPoint, options, dabIndexStart = 0, shouldIncludeStart = false) {
  if (!ctx || !startPoint || !options || !options.brushPreview) return dabIndexStart;

  let dabIndex = Math.max(0, Number(dabIndexStart) || 0);
  const spacingRatio = clamp(Number(options.spacing) || 0.08, 0.01, 2);
  const getBlobSpacing = (point) => {
    const pressure = pressureValue(point, 1);
    const spacingMultiplier = options.pressureToSpacing === true ? clamp(evaluateCurve(options.spacingCurve, pressure, pressure), 0.05, 4) : 1;
    return Math.max(1, (Number(options.size) || 1) * spacingRatio * spacingMultiplier);
  };
  const spacing = getBlobSpacing(startPoint);
  const scatter = Math.max(0, Number(options.scatter) || 0);
  const rotationMode = String(options.rotationMode || 'stroke');
  const runtimeState = getStrokeRuntimeState(options);
  const baseAngle = pointAngle(startPoint, endPoint, runtimeState && Number.isFinite(Number(runtimeState.lastAngle)) ? Number(runtimeState.lastAngle) : 0);

  function drawAt(point, angle, index) {
    const seed = (Number(options.strokeSeed) || 1) + index * 101;
    const scatterAngle = randomAngle(seed);
    const pointPressure = getPointPressure(point, 1);
    const scatterDistance = scatter > 0 ? scatter * (Number(options.size) || 1) * pointPressure * 0.5 * Math.abs(Math.sin(seed)) : 0;
    const rotation = resolveRotation(rotationMode, point, angle, seed, runtimeState && Number.isFinite(Number(runtimeState.lastAngle)) ? Number(runtimeState.lastAngle) : 0);

    renderImportedBrushBlobDab(ctx, {
      brushPreview: options.brushPreview,
      x: Number(point.x) + Math.cos(scatterAngle) * scatterDistance,
      y: Number(point.y) + Math.sin(scatterAngle) * scatterDistance,
      point,
      size: options.size,
      opacity: options.opacity,
      color: options.color || options.strokeColor || '#000000',
      rotation,
      currentDabSeed: seed,
      isEraser: options.isEraser,
      tool: options.isEraser ? 'eraser' : 'pen',
      pressureToSize: options.pressureToSize,
      pressureToOpacity: options.pressureToOpacity,
      pressureToFlow: options.pressureToFlow,
      tipScale: options.tipScale,
      fixedAngle: options.fixedAngle,
      flow: options.flow,
      brushOpacity: options.brushOpacity,
      sizeCurve: options.sizeCurve,
      opacityCurve: options.opacityCurve,
      flowCurve: options.flowCurve,
      spacingCurve: options.spacingCurve,
      pressureToSpacing: options.pressureToSpacing,
      softness: options.softness,
    });
    if (runtimeState) runtimeState.lastAngle = rotation;
  }

  if (shouldIncludeStart) {
    drawAt(startPoint, baseAngle, dabIndex);
    dabIndex += 1;
    resetStrokeRuntimeState(runtimeState, spacing);
  }

  if (!endPoint) return dabIndex;

  const dx = Number(endPoint.x) - Number(startPoint.x);
  const dy = Number(endPoint.y) - Number(startPoint.y);
  const distance = Math.hypot(dx, dy);

  if (distance <= 0.0001) {
    if (!shouldIncludeStart && (!runtimeState || dabIndex === 0)) {
      drawAt(endPoint, baseAngle, dabIndex);
      dabIndex += 1;
      resetStrokeRuntimeState(runtimeState, spacing);
    }
    return dabIndex;
  }

  let distanceToNext = runtimeState && Number.isFinite(Number(runtimeState.distanceToNext))
    ? Math.max(0.0001, Number(runtimeState.distanceToNext))
    : 0;

  while (distanceToNext <= distance) {
    const t = clamp(distanceToNext / distance, 0, 1);
    const pressureA = Number(startPoint.pressure);
    const pressureB = Number(endPoint.pressure);
    const pressure = Number.isFinite(pressureA) || Number.isFinite(pressureB)
      ? (Number.isFinite(pressureA) ? pressureA : 1) + (((Number.isFinite(pressureB) ? pressureB : 1) - (Number.isFinite(pressureA) ? pressureA : 1)) * t)
      : undefined;

    const currentPoint = {
      x: Number(startPoint.x) + dx * t,
      y: Number(startPoint.y) + dy * t,
      pressure,
    };

    drawAt(currentPoint, baseAngle, dabIndex);

    dabIndex += 1;
    distanceToNext += getBlobSpacing(currentPoint);
  }

  markStrokeRuntimeProgress(runtimeState, distanceToNext - distance);
  return dabIndex;
}

function drawImportedBrushImageDab(ctx, point, options, angle) {
  const brush = options && options.importedBrush ? options.importedBrush : null;
  const canvases = brush && Array.isArray(brush.originalTipCanvases) ? brush.originalTipCanvases.filter(Boolean) : [];
  const seed = Number(options && options.currentDabSeed);
  const canvas = canvases.length ? canvases[Math.abs(Math.floor(Number.isFinite(seed) ? seed : 0)) % canvases.length] : brush && brush.originalTipCanvas;
  if (!ctx || !point || !canvas) return false;

  const isEraser = !!(options && options.isEraser);
  const size = Math.max(0.5, (Number(options.size) || 1) * (isEraser ? 1 : pressureSizeMultiplier(point, options)));
  const opacity = isEraser ? clampOpacity(options && options.opacity == null ? 1 : options.opacity) : brushPaintOpacity(options, point);
  const softness = isEraser ? 0 : brushSoftnessPercent(options);
  const grainedCanvas = !isEraser ? getProcreateGrainedTipCanvas(canvas, brush && brush.originalGrainCanvas, brush, seed) : canvas;
  const sourceCanvas = isEraser ? canvas : (getSoftnessAdjustedTipCanvas(grainedCanvas || canvas, softness) || grainedCanvas || canvas);
  const width = Math.max(1, sourceCanvas.width || 1);
  const height = Math.max(1, sourceCanvas.height || 1);
  const scale = size / Math.max(width, height);
  const drawWidth = width * scale;
  const drawHeight = height * scale;

  ctx.save();
  ctx.globalCompositeOperation = isEraser ? 'destination-out' : 'source-over';
  ctx.globalAlpha = opacity;
  ctx.imageSmoothingEnabled = isEraser ? false : softness > 0;
  if ('imageSmoothingQuality' in ctx) ctx.imageSmoothingQuality = isEraser || softness <= 0 ? 'low' : 'high';
  ctx.translate(point.x, point.y);
  ctx.rotate((Number(angle) || 0) + (Number(brush && brush.fixedAngle) || 0));

  if (isEraser) {
    ctx.drawImage(sourceCanvas, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
  } else {
    const tintedCanvas = getTintedTipCanvas(sourceCanvas, options.color || options.strokeColor || '#000000');
    ctx.drawImage(tintedCanvas || sourceCanvas, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);

    const wetAmount = clamp((Number(brush && brush.procreateWetEdgesAmount) || 0) + (Number(brush && brush.procreateBurntEdgesAmount) || 0) * 0.65, 0, 1);
    if (wetAmount > 0.01) {
      ctx.globalCompositeOperation = 'source-atop';
      ctx.globalAlpha = opacity * wetAmount * 0.26;
      ctx.lineWidth = Math.max(1, Math.min(drawWidth, drawHeight) * 0.08);
      ctx.strokeStyle = options.color || options.strokeColor || '#000000';
      ctx.strokeRect(-drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
    }
  }

  ctx.restore();
  return true;
}

export function drawImportedBrushImageSegment(ctx, startPoint, endPoint, options, dabIndexStart = 0, shouldIncludeStart = false) {
  if (!ctx || !startPoint || !options || !options.importedBrush) return dabIndexStart;

  let dabIndex = Math.max(0, Number(dabIndexStart) || 0);
  const getImageSpacing = (point) => getImportedBrushSpacing(options, point);
  const spacing = getImageSpacing(startPoint);
  const scatter = Math.max(0, Number(options.importedBrush.scatter || options.scatter) || 0);
  const rotationMode = String(options.importedBrush.rotationMode || options.rotationMode || 'stroke');
  const runtimeState = getStrokeRuntimeState(options);
  const baseAngle = pointAngle(startPoint, endPoint, runtimeState && Number.isFinite(Number(runtimeState.lastAngle)) ? Number(runtimeState.lastAngle) : 0);

  function drawAt(point, angle, index) {
    const seed = (Number(options.strokeSeed) || 1) + index * 101;
    const scatterAngle = randomAngle(seed);
    const pointPressure = getPointPressure(point, 1);
    const scatterDistance = scatter > 0 ? scatter * (Number(options.size) || 1) * pointPressure * 0.5 * Math.abs(Math.sin(seed)) : 0;
    const rotation = resolveRotation(rotationMode, point, angle, seed, runtimeState && Number.isFinite(Number(runtimeState.lastAngle)) ? Number(runtimeState.lastAngle) : 0);
    const countBase = Math.max(1, Math.min(16, Math.round(Number(options.importedBrush.procreateShapeCount) || 1)));
    const countJitter = clamp(Number(options.importedBrush.procreateShapeCountJitter) || 0, 0, 1);
    const count = countBase <= 1 ? 1 : Math.max(1, Math.round(countBase - countJitter * randomUnit(seed + 401) * (countBase - 1)));
    const extraScatter = Math.max(0, Number(options.importedBrush.procreateShapeScatter) || 0) * (Number(options.size) || 1) * 0.36;

    for (let dabPart = 0; dabPart < count; dabPart += 1) {
      const partSeed = seed + dabPart * 577;
      const partAngle = randomAngle(partSeed + 29);
      const partDistance = dabPart === 0 ? 0 : extraScatter * (0.18 + randomUnit(partSeed + 31) * 0.82);
      drawImportedBrushImageDab(ctx, {
        ...point,
        x: Number(point.x) + Math.cos(scatterAngle) * scatterDistance + Math.cos(partAngle) * partDistance,
        y: Number(point.y) + Math.sin(scatterAngle) * scatterDistance + Math.sin(partAngle) * partDistance,
      }, { ...options, currentDabSeed: partSeed }, rotation);
    }
    if (runtimeState) runtimeState.lastAngle = rotation;
  }

  if (shouldIncludeStart) {
    drawAt(startPoint, baseAngle, dabIndex);
    dabIndex += 1;
    resetStrokeRuntimeState(runtimeState, spacing);
  }

  if (!endPoint) return dabIndex;

  const dx = Number(endPoint.x) - Number(startPoint.x);
  const dy = Number(endPoint.y) - Number(startPoint.y);
  const distance = Math.hypot(dx, dy);

  if (distance <= 0.0001) {
    if (!shouldIncludeStart && (!runtimeState || dabIndex === 0)) {
      drawAt(endPoint, baseAngle, dabIndex);
      dabIndex += 1;
      resetStrokeRuntimeState(runtimeState, spacing);
    }
    return dabIndex;
  }

  let distanceToNext = runtimeState && Number.isFinite(Number(runtimeState.distanceToNext))
    ? Math.max(0.0001, Number(runtimeState.distanceToNext))
    : 0;

  while (distanceToNext <= distance) {
    const t = clamp(distanceToNext / distance, 0, 1);
    const pressureA = Number(startPoint.pressure);
    const pressureB = Number(endPoint.pressure);
    const pressure = Number.isFinite(pressureA) || Number.isFinite(pressureB)
      ? (Number.isFinite(pressureA) ? pressureA : 1) + (((Number.isFinite(pressureB) ? pressureB : 1) - (Number.isFinite(pressureA) ? pressureA : 1)) * t)
      : undefined;

    const currentPoint = {
      x: Number(startPoint.x) + dx * t,
      y: Number(startPoint.y) + dy * t,
      pressure,
    };

    drawAt(currentPoint, baseAngle, dabIndex);

    dabIndex += 1;
    distanceToNext += getImageSpacing(currentPoint);
  }

  markStrokeRuntimeProgress(runtimeState, distanceToNext - distance);
  return dabIndex;
}


export function buildImportedBrushGpuDabs(startPoint, endPoint, options, dabIndexStart = 0, shouldIncludeStart = false) {
  if (!startPoint || !options || !options.importedBrush) {
    return { dabs: [], dabIndex: dabIndexStart, distanceToNext: 0, lastAngle: 0 };
  }

  const brush = options.importedBrush;
  const runtimeState = getStrokeRuntimeState(options);
  if (isV2ImportedBrush(brush)) {
    return planDabs(startPoint, endPoint, brush, options, runtimeState, dabIndexStart, shouldIncludeStart);
  }
  let dabIndex = Math.max(0, Number(dabIndexStart) || 0);
  const dabs = [];
  const spacingAt = (point) => getImportedBrushSpacing(options, point);
  const baseSpacing = spacingAt(startPoint);
  const scatter = Math.max(0, Number(brush.scatter || options.scatter) || 0);
  const rotationMode = String(brush.rotationMode || options.rotationMode || 'stroke');
  const fallbackAngle = runtimeState && Number.isFinite(Number(runtimeState.lastAngle)) ? Number(runtimeState.lastAngle) : 0;
  const baseAngle = pointAngle(startPoint, endPoint, fallbackAngle);

  const addDab = (point, angle, index) => {
    const seed = (Number(options.strokeSeed) || 1) + index * 101;
    const scatterAngle = randomAngle(seed);
    const pointPressure = getPointPressure(point, 1);
    const scatterDistance = scatter > 0
      ? scatter * (Number(options.size) || 1) * pointPressure * 0.5 * Math.abs(Math.sin(seed))
      : 0;
    const rotation = resolveRotation(rotationMode, point, angle, seed, fallbackAngle);
    const countBase = Math.max(1, Math.min(16, Math.round(Number(brush.procreateShapeCount) || 1)));
    const countJitter = clamp(Number(brush.procreateShapeCountJitter) || 0, 0, 1);
    const count = countBase <= 1
      ? 1
      : Math.max(1, Math.round(countBase - countJitter * randomUnit(seed + 401) * (countBase - 1)));
    const extraScatter = Math.max(0, Number(brush.procreateShapeScatter) || 0) * (Number(options.size) || 1) * 0.36;
    const plotJitter = Math.max(0, Number(brush.procreatePlotJitter) || 0) * (Number(options.size) || 1) * 0.36;

    for (let dabPart = 0; dabPart < count; dabPart += 1) {
      const partSeed = seed + dabPart * 577;
      const partAngle = randomAngle(partSeed + 29);
      const partDistance = dabPart === 0 ? 0 : extraScatter * (0.18 + randomUnit(partSeed + 31) * 0.82);
      const jitterAngle = randomAngle(partSeed + 71);
      const jitterDistance = plotJitter > 0 ? plotJitter * randomUnit(partSeed + 73) : 0;
      const sizeMultiplier = pressureSizeMultiplier(point, options);
      dabs.push({
        x: Number(point.x) + Math.cos(scatterAngle) * scatterDistance + Math.cos(partAngle) * partDistance + Math.cos(jitterAngle) * jitterDistance,
        y: Number(point.y) + Math.sin(scatterAngle) * scatterDistance + Math.sin(partAngle) * partDistance + Math.sin(jitterAngle) * jitterDistance,
        pressure: getPointPressure(point, 1),
        sizeMultiplier,
        opacity: brushPaintOpacity(options, point),
        rotation: rotation + (Number(brush.fixedAngle) || 0),
        seed: partSeed,
        tipIndex: Math.abs(Math.floor(partSeed)),
      });
    }

    if (runtimeState) runtimeState.lastAngle = rotation;
  };

  if (shouldIncludeStart) {
    addDab(startPoint, baseAngle, dabIndex);
    dabIndex += 1;
    resetStrokeRuntimeState(runtimeState, baseSpacing);
  }

  if (!endPoint) {
    return {
      dabs,
      dabIndex,
      distanceToNext: runtimeState ? runtimeState.distanceToNext : baseSpacing,
      lastAngle: runtimeState ? runtimeState.lastAngle : baseAngle,
    };
  }

  const dx = Number(endPoint.x) - Number(startPoint.x);
  const dy = Number(endPoint.y) - Number(startPoint.y);
  const distance = Math.hypot(dx, dy);

  if (distance <= 0.0001) {
    if (!shouldIncludeStart && (!runtimeState || dabIndex === 0)) {
      addDab(endPoint, baseAngle, dabIndex);
      dabIndex += 1;
      resetStrokeRuntimeState(runtimeState, baseSpacing);
    }
    return {
      dabs,
      dabIndex,
      distanceToNext: runtimeState ? runtimeState.distanceToNext : baseSpacing,
      lastAngle: runtimeState ? runtimeState.lastAngle : baseAngle,
    };
  }

  let distanceToNext = runtimeState && Number.isFinite(Number(runtimeState.distanceToNext))
    ? Math.max(0.0001, Number(runtimeState.distanceToNext))
    : 0;

  while (distanceToNext <= distance) {
    const t = clamp(distanceToNext / distance, 0, 1);
    const pressureA = Number(startPoint.pressure);
    const pressureB = Number(endPoint.pressure);
    const pressure = Number.isFinite(pressureA) || Number.isFinite(pressureB)
      ? (Number.isFinite(pressureA) ? pressureA : 1) + (((Number.isFinite(pressureB) ? pressureB : 1) - (Number.isFinite(pressureA) ? pressureA : 1)) * t)
      : undefined;
    const tiltXA = Number(startPoint.tiltX);
    const tiltXB = Number(endPoint.tiltX);
    const tiltYA = Number(startPoint.tiltY);
    const tiltYB = Number(endPoint.tiltY);
    const currentPoint = {
      x: Number(startPoint.x) + dx * t,
      y: Number(startPoint.y) + dy * t,
      pressure,
      tiltX: Number.isFinite(tiltXA) || Number.isFinite(tiltXB)
        ? (Number.isFinite(tiltXA) ? tiltXA : 0) + (((Number.isFinite(tiltXB) ? tiltXB : 0) - (Number.isFinite(tiltXA) ? tiltXA : 0)) * t)
        : undefined,
      tiltY: Number.isFinite(tiltYA) || Number.isFinite(tiltYB)
        ? (Number.isFinite(tiltYA) ? tiltYA : 0) + (((Number.isFinite(tiltYB) ? tiltYB : 0) - (Number.isFinite(tiltYA) ? tiltYA : 0)) * t)
        : undefined,
    };

    addDab(currentPoint, baseAngle, dabIndex);
    dabIndex += 1;
    distanceToNext += spacingAt(currentPoint);
  }

  markStrokeRuntimeProgress(runtimeState, distanceToNext - distance);
  return {
    dabs,
    dabIndex,
    distanceToNext: runtimeState ? runtimeState.distanceToNext : distanceToNext - distance,
    lastAngle: runtimeState ? runtimeState.lastAngle : baseAngle,
  };
}


export function buildImportedBrushGpuCurveDabs(previousPreviousPoint, previousPoint, currentPoint, options, dabIndexStart = 0) {
  if (!previousPreviousPoint || !previousPoint || !currentPoint || !options || !options.importedBrush) {
    return { dabs: [], dabIndex: dabIndexStart, distanceToNext: 0, lastAngle: 0 };
  }

  const curveStart = midpointImportedPoint(previousPreviousPoint, previousPoint);
  const curveEnd = midpointImportedPoint(previousPoint, currentPoint);
  const points = buildQuadraticImportedPoints(curveStart, previousPoint, curveEnd, options);
  const dabs = [];
  let dabIndex = Math.max(0, Number(dabIndexStart) || 0);
  let distanceToNext = 0;
  let lastAngle = 0;

  for (let index = 1; index < points.length; index += 1) {
    const plan = buildImportedBrushGpuDabs(
      points[index - 1],
      points[index],
      options,
      dabIndex,
      false,
    );
    if (Array.isArray(plan.dabs) && plan.dabs.length) dabs.push(...plan.dabs);
    dabIndex = Math.max(dabIndex, Number(plan.dabIndex) || dabIndex);
    distanceToNext = Number(plan.distanceToNext) || distanceToNext;
    lastAngle = Number(plan.lastAngle) || lastAngle;
  }

  return { dabs, dabIndex, distanceToNext, lastAngle };
}

export function buildImportedBrushGpuFinalTailDabs(previousPoint, currentPoint, options, dabIndexStart = 0) {
  if (!previousPoint || !currentPoint || !options || !options.importedBrush) {
    return { dabs: [], dabIndex: dabIndexStart, distanceToNext: 0, lastAngle: 0 };
  }

  const tailStart = midpointImportedPoint(previousPoint, currentPoint);
  return buildImportedBrushGpuDabs(
    tailStart,
    currentPoint,
    options,
    dabIndexStart,
    false,
  );
}

export function getImportedBrushGpuTipCanvas(brush, seed = 0, softness = 0, isEraser = false) {
  if (!brush) return null;
  const canvases = Array.isArray(brush.originalTipCanvases) ? brush.originalTipCanvases.filter(Boolean) : [];
  const canvas = canvases.length
    ? canvases[Math.abs(Math.floor(Number(seed) || 0)) % canvases.length]
    : brush.originalTipCanvas;
  if (!canvas) return null;
  if (isEraser) return canvas;
  const grainedCanvas = getProcreateGrainedTipCanvas(canvas, brush.originalGrainCanvas, brush, seed);
  return getSoftnessAdjustedTipCanvas(grainedCanvas || canvas, softness) || grainedCanvas || canvas;
}

export function drawImportedBrushImageSegmentWithinSelection(ctx, startPoint, endPoint, options, selection, dabIndexStart = 0, shouldIncludeStart = false) {
  if (!ctx) return dabIndexStart;
  if (!selection || !selection.bounds) {
    return drawImportedBrushImageSegment(ctx, startPoint, endPoint, options, dabIndexStart, shouldIncludeStart);
  }

  const clipped = clipContextToSelection(ctx, selection);
  const nextDabIndex = drawImportedBrushImageSegment(ctx, startPoint, endPoint, options, dabIndexStart, shouldIncludeStart);
  if (clipped) ctx.restore();
  return nextDabIndex;
}

export function drawImportedBrushBlobSegmentWithinSelection(ctx, startPoint, endPoint, options, selection, dabIndexStart = 0, shouldIncludeStart = false) {
  if (!ctx) return dabIndexStart;
  if (!selection || !selection.bounds) {
    return drawImportedBrushBlobSegment(ctx, startPoint, endPoint, options, dabIndexStart, shouldIncludeStart);
  }

  const clipped = clipContextToSelection(ctx, selection);
  const nextDabIndex = drawImportedBrushBlobSegment(ctx, startPoint, endPoint, options, dabIndexStart, shouldIncludeStart);
  if (clipped) ctx.restore();
  return nextDabIndex;
}

export function drawImportedBrushImageCurveSegmentWithinSelection(ctx, previousPreviousPoint, previousPoint, currentPoint, options, selection, dabIndexStart = 0) {
  return drawImportedCurveUsingSegment(
    drawImportedBrushImageSegmentWithinSelection,
    ctx,
    previousPreviousPoint,
    previousPoint,
    currentPoint,
    options,
    selection,
    dabIndexStart,
  );
}

export function drawImportedBrushImageFinalTailWithinSelection(ctx, previousPoint, currentPoint, options, selection, dabIndexStart = 0) {
  return drawImportedFinalTailUsingSegment(
    drawImportedBrushImageSegmentWithinSelection,
    ctx,
    previousPoint,
    currentPoint,
    options,
    selection,
    dabIndexStart,
  );
}

export function drawImportedBrushBlobCurveSegmentWithinSelection(ctx, previousPreviousPoint, previousPoint, currentPoint, options, selection, dabIndexStart = 0) {
  return drawImportedCurveUsingSegment(
    drawImportedBrushBlobSegmentWithinSelection,
    ctx,
    previousPreviousPoint,
    previousPoint,
    currentPoint,
    options,
    selection,
    dabIndexStart,
  );
}

export function drawImportedBrushBlobFinalTailWithinSelection(ctx, previousPoint, currentPoint, options, selection, dabIndexStart = 0) {
  return drawImportedFinalTailUsingSegment(
    drawImportedBrushBlobSegmentWithinSelection,
    ctx,
    previousPoint,
    currentPoint,
    options,
    selection,
    dabIndexStart,
  );
}

